// C57 D2: botao "Gerar com IA" reutilizavel. Chama POST
// /api/channels/:slug/products/:sku/ai/generate (apps/api/src/ai/generate.ts)
// e entrega o resultado pra revisao humana via onResult, NUNCA salva sozinho
// (quem salva sao os botoes "Salvar base"/"Salvar override"/"Salvar SEO" ja
// existentes na ficha do produto).
import { useMutation } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import type { AiTarget } from "../lib/types";

interface AiGenerateTextButtonProps {
  slug: string;
  sku: string;
  target: AiTarget;
  onResult: (text: string) => void;
  label?: string;
}

/**
 * Variante para os 4 targets que devolvem texto pronto pra colar num campo
 * (description_short, description_long, seo_title, seo_meta_description).
 */
export function AiGenerateButton({
  slug,
  sku,
  target,
  onResult,
  label = "Gerar com IA",
}: AiGenerateTextButtonProps): JSX.Element | null {
  const { can } = useAuth();
  const mut = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ text: string }>(
        `/api/channels/${slug}/products/${sku}/ai/generate`,
        { target },
        { timeout: 65_000 },
      );
      return r.data.text;
    },
    onSuccess: (text) => {
      onResult(text);
      toast.success("Texto gerado, revise antes de salvar");
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // RBAC configuravel (BUG-01 do QA): sem ai.generate o botao nao aparece —
  // a API ja negava com 403, isto so alinha a UI ao que ela realmente pode.
  if (!can("ai.generate")) return null;

  return (
    <span className="stack" style={{ gap: ".25rem", alignItems: "flex-start" }}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => mut.mutate()}
        loading={mut.isPending}
      >
        {!mut.isPending ? <Sparkles className="h-3.5 w-3.5" aria-hidden /> : null}
        {mut.isPending ? "Gerando… (pode levar até 1 min)" : label}
      </Button>
      {/* Erro FICA ao lado do botao ate a proxima tentativa: o toast sumia em
          4,5 s e o dono via "nao funciona" sem motivo (print 23/09). */}
      {mut.isError ? (
        <span role="alert" className="field__hint" style={{ color: "var(--color-bad)" }}>
          {apiErrorMessage(mut.error)}
        </span>
      ) : null}
    </span>
  );
}

interface AiGenerateTagsButtonProps {
  slug: string;
  sku: string;
  onResult: (tags: string[]) => void;
}

/**
 * Variante para o target tags_suggest, que devolve { tags: string[] } em vez
 * de { text: string }.
 */
export function AiGenerateTagsButton({
  slug,
  sku,
  onResult,
}: AiGenerateTagsButtonProps): JSX.Element | null {
  const { can } = useAuth();
  const mut = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ tags: string[] }>(
        `/api/channels/${slug}/products/${sku}/ai/generate`,
        { target: "tags_suggest" },
        { timeout: 65_000 },
      );
      return r.data.tags;
    },
    onSuccess: (tags) => {
      onResult(tags);
      if (tags.length === 0) {
        toast.info("A IA não sugeriu tags pra este produto");
      } else {
        toast.success("Tags sugeridas, revise antes de usar");
      }
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (!can("ai.generate")) return null;

  return (
    <span className="stack" style={{ gap: ".25rem", alignItems: "flex-start" }}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => mut.mutate()}
        loading={mut.isPending}
      >
        {!mut.isPending ? <Sparkles className="h-3.5 w-3.5" aria-hidden /> : null}
        {mut.isPending ? "Gerando… (pode levar até 1 min)" : "Sugerir tags com IA"}
      </Button>
      {/* Erro FICA ao lado do botao ate a proxima tentativa: o toast sumia em
          4,5 s e o dono via "nao funciona" sem motivo (print 23/09). */}
      {mut.isError ? (
        <span role="alert" className="field__hint" style={{ color: "var(--color-bad)" }}>
          {apiErrorMessage(mut.error)}
        </span>
      ) : null}
    </span>
  );
}

export interface AiAttributeSuggestion {
  id: string;
  name: string;
  value_id: string;
  value_name: string;
}

/**
 * Variante do alvo attributes_suggest (so canal Mercado Livre): devolve { suggestions } com SO valores que existem
 * na lista do ML. Nunca salva: a ficha mostra as escolhas e a pessoa confirma.
 */
export function AiSuggestAttributesButton({
  slug,
  sku,
  onResult,
}: {
  slug: string;
  sku: string;
  onResult: (s: AiAttributeSuggestion[]) => void;
}): JSX.Element | null {
  const { can } = useAuth();
  const mut = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ suggestions: AiAttributeSuggestion[] }>(
        `/api/channels/${slug}/products/${sku}/ai/generate`,
        { target: "attributes_suggest" },
        { timeout: 65_000 },
      );
      return r.data.suggestions;
    },
    onSuccess: (s) => {
      onResult(s);
      if (s.length === 0) toast.info("A IA não encontrou valores seguros para sugerir neste produto");
      else toast.success(`${s.length} sugestão(ões) da IA, revise antes de salvar`);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  if (!can("ai.generate")) return null;
  return (
    <span className="stack" style={{ gap: ".25rem", alignItems: "flex-start" }}>
      <Button type="button" variant="ghost" size="sm" onClick={() => mut.mutate()} loading={mut.isPending}>
        {!mut.isPending ? <Sparkles className="h-3.5 w-3.5" aria-hidden /> : null}
        {mut.isPending ? "Gerando… (pode levar até 1 min)" : "Sugerir com IA"}
      </Button>
      {mut.isError ? (
        <span role="alert" className="field__hint" style={{ color: "var(--color-bad)" }}>
          {apiErrorMessage(mut.error)}
        </span>
      ) : null}
    </span>
  );
}
